import * as dotProp from 'dot-prop-immutable';
import { Action } from 'typescript-fsa';
import _ from 'lodash';

import {
  createReducer,
  getArrayAfterAsyncCall,
  updateArrayItemAfterAsyncCall,
} from '@topconagriculture/frontend-utils';

import { Preset, PresetSettings } from '../../../core/api/preset/preset.types';
import {
  changeFeedingListPreset,
  createPreset,
  deletePreset,
  fetchPresetList,
  updateRefusalsPreset,
  setSortableColumn,
  setSortingOrder,
  updateFeedingListPreset,
  updatePreset,
  saveAndApplyStockTransactionsPreset,
  updateStockTransactionsPreset,
} from './preset.actions';
import { feedingActions } from '../../actions';
import { getRefusalsPreset } from '../refusals/refusals.actions';
import { SortingOrder } from '../reports/reports.types';

export type PresetState = Preset[];
export const initialState: PresetState = [];

const presetReducer = createReducer(initialState, {
  [fetchPresetList.done.type]: (state: PresetState, action: ReturnType<typeof fetchPresetList.done>) =>
    getArrayAfterAsyncCall<PresetSettings>(state, action),
  [feedingActions.getFeedingListPresets.done.type]: (state: PresetState, action: Action<any>) => {
    return [...state, { reportType: 'feedingList', ...action.payload.result[0] }];
  },
  [updatePreset.done.type]: (state: PresetState, action: Action<any>) =>
    updateArrayItemAfterAsyncCall<Preset>(state, action),
  [deletePreset.done.type]: (state: PresetState, action: ReturnType<typeof deletePreset.done>) => {
    const id: Preset['id'] = action.payload.params.data.id;
    const indexFound = state.findIndex((preset: Preset) => preset.id === id);
    return dotProp.delete(state, indexFound);
  },
  [createPreset.done.type]: (state: PresetState, action: ReturnType<typeof createPreset.done>) => {
    return [...state, action.payload.result];
  },
  [changeFeedingListPreset.type]: (state: PresetState, action: Action<any>) => {
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'feedingList');
    const {
      dataSettings: { sortingColumn, sortingOrder },
      ...viewSettings
    } = action.payload.data;

    const stateCopy = _.cloneDeep(state);

    stateCopy[presetIndex].dataSettings = { sortingColumn, sortingOrder };
    stateCopy[presetIndex].viewSettings = viewSettings.viewSettings;

    return [...stateCopy];
  },
  [updateFeedingListPreset.type]: (state: PresetState, action: Action<Preset>) => {
    const presets = _.cloneDeep(state);
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'feedingList');
    presets[presetIndex] = { ...action.payload, reportType: 'feedingList' };

    return [...presets];
  },
  [setSortableColumn.type]: (state: PresetState, action: Action<string>) => {
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'feedingList');
    const sortingColumn = action.payload;
    const stateCopy = _.cloneDeep(state);
    stateCopy[presetIndex].dataSettings = { ...stateCopy[presetIndex].dataSettings, sortingColumn };
    return [...stateCopy];
  },
  [setSortingOrder.type]: (state: PresetState, action: Action<SortingOrder>) => {
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'feedingList');
    const sortingOrder = action.payload;
    const stateCopy = _.cloneDeep(state);
    stateCopy[presetIndex].dataSettings = { ...stateCopy[presetIndex].dataSettings, sortingOrder };
    return [...stateCopy];
  },
  [getRefusalsPreset.done.type]: (state: PresetState, action: Action<any>) => {
    return [...state, { reportType: 'refusals', ...action.payload.result[0] }];
  },
  [updateRefusalsPreset.type]: (state: PresetState, action: ReturnType<typeof updateRefusalsPreset>) => {
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'refusals');
    const stateCopy = _.cloneDeep(state);
    stateCopy[presetIndex] = {
      ...stateCopy[presetIndex],
      ...action.payload,
      dataSettings: {
        ...stateCopy[presetIndex].dataSettings,
        ...action.payload.dataSettings,
        daysBack: Number(action.payload.dataSettings.daysBack),
      },
      viewSettings: {
        ...stateCopy[presetIndex].viewSettings,
        ...action.payload.viewSettings,
        // collapsed is value of radiobutton. this value is string and has to be transformed to boolean
        collapsed: action.payload.viewSettings.collapsed.toString() === 'true',
      },
    };
    return [...stateCopy];
  },
  [updateStockTransactionsPreset.type]: (
    state: PresetState,
    action: ReturnType<typeof updateStockTransactionsPreset>,
  ) => {
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'StockTransaction');
    const stateCopy = _.cloneDeep(state);
    stateCopy[presetIndex] = {
      ...stateCopy[presetIndex],
      ...action.payload,
      dataSettings: {
        ...stateCopy[presetIndex].dataSettings,
        ...action.payload.dataSettings,
        daysBack: Number(action.payload.dataSettings.daysBack),
      },
      viewSettings: {
        ...stateCopy[presetIndex].viewSettings,
        ...action.payload.viewSettings,
        collapsed: action.payload.viewSettings.collapsed?.toString() === 'true',
      },
    };
    return [...stateCopy];
  },
  [saveAndApplyStockTransactionsPreset.done.type]: (
    state: PresetState,
    action: ReturnType<typeof saveAndApplyStockTransactionsPreset.done>,
  ) => {
    const presetIndex = state.findIndex(({ reportType }) => reportType === 'StockTransaction');
    const stateCopy = _.cloneDeep(state);
    const preset = action.payload.result;
    stateCopy[presetIndex] = {
      ...stateCopy[presetIndex],
      ...action.payload,
      dataSettings: {
        ...stateCopy[presetIndex].dataSettings,
        ...preset.dataSettings,
        daysBack: Number(preset.dataSettings.daysBack),
      },
      viewSettings: {
        ...stateCopy[presetIndex].viewSettings,
        ...preset.viewSettings,
        collapsed: preset.viewSettings.collapsed?.toString() === 'true',
      },
    };
    return [...stateCopy];
  },
});

export default presetReducer;
